import { camelCase, map, set, transform } from 'lodash-es'
import { z } from 'zod'

import { useTelemetry } from '@/feature/telemetry/api/useTelemetry'

export const AssetLocationHistorySchema = z
  .array(
    z
      .object({
        'device-timestamp': z.coerce.date(),
        latitude: z.number(),
        longitude: z.number(),
        speed: z.number().optional(),
        heading: z.number().optional(),
      })
      .passthrough(),
  )
  .or(z.undefined())
  .transform((data) =>
    map(data, (d) =>
      transform(
        d,
        (acc, value, key) => set(acc, camelCase(key), value),
        {} as Omit<typeof d, 'device-timestamp'> & {
          deviceTimestamp: Date
          latitude: number
          longitude: number
          speed?: number
          heading?: number
        },
      ),
    ),
  )

export type AssetLocationHistory = z.infer<typeof AssetLocationHistorySchema>

export const useAssetLocationHistory = <
  SWROptions extends Parameters<typeof useTelemetry<AssetLocationHistory>>[2],
>(
  // null skips the fetch, for callers that already hold the history (e.g. a task's trip trail).
  telemetryId: string | null,
  period: string = '48h',
  stepSize = '1s',
  swrOptions?: SWROptions,
) => {
  const res = useTelemetry<AssetLocationHistory>(
    telemetryId == null ? null : `v2/api/location-replay/${encodeURIComponent(telemetryId)}`,
    {
      searchParams: {
        time_duration: period,
        max_resolution: stepSize,
      },
    },
    swrOptions,
  )

  return {
    ...res,
    data: AssetLocationHistorySchema.parse(res.data),
  }
}

export const usePreloadAssetLocationHistory = (
  telemetryId: string,
  period: string = '48h',
  stepSize = '1s',
) => useAssetLocationHistory(telemetryId, period, stepSize)
